import type { TagType } from "@/components/Tag/Tag";
import type { FlagKey } from "@/hooks/useFeatureFlags/flags";
import { isExecuSearchServiceLevel, PostStage, ServiceLevel } from "@/pages/_layouts/jobs/types";
import { JobApplicantCountEstimateMatchTier, type JobApplicantCountEstimate } from "./queries";

export const JOB_APPLICANT_COUNT_ESTIMATE_FLAG = "backoffice-job-applicant-count-estimate" satisfies FlagKey;

export const APPLY_VIA_FACILITY_WEBSITE = "Facility Website";

export const LOW_CONFIDENCE_SAMPLE_THRESHOLD = 10;
export const LOW_ESTIMATE_MEDIAN_THRESHOLD = 10;

export const RANGE_LABEL = "Estimated PGA.org Applications by Deadline";

export const INCOMPLETE_CRITERIA_TITLE = "Enter all required job details to see an estimated application count.";
export const INCOMPLETE_CRITERIA_CAPTION = "Compensation, location, and the other required fields are needed first.";

export const FACILITY_WEBSITE_DESCRIPTION = "This estimate is for PGA.org applications only.";
export const FACILITY_WEBSITE_SUBTEXT =
  "This post collects applications on a facility website. Those applications are not included.";

export const LOW_CONFIDENCE_COPY = "Few similar PGA.org posts match this job, so treat the range as approximate.";

export const EXECUSEARCH_COPY = "Use this range in employer conversations and to plan applicant review work.";

export const SELF_SERVE_IN_REVIEW_COACHING =
  "This estimate is below typical. Discuss compensation or requirements with the employer.";

export const MATCH_TIER_LABELS: Record<JobApplicantCountEstimateMatchTier, string> = {
  [JobApplicantCountEstimateMatchTier.EXACT]: "Exact",
  [JobApplicantCountEstimateMatchTier.HIGH]: "High",
  [JobApplicantCountEstimateMatchTier.MEDIUM]: "Medium",
  [JobApplicantCountEstimateMatchTier.LOW]: "Low",
  [JobApplicantCountEstimateMatchTier.GLOBAL]: "Broad"
};

export function isFacilityWebsiteApplyVia(applyVia: string | null | undefined): boolean {
  return applyVia === APPLY_VIA_FACILITY_WEBSITE;
}

export function isLowConfidenceEstimate(estimate: JobApplicantCountEstimate): boolean {
  return (
    estimate.sampleSize < LOW_CONFIDENCE_SAMPLE_THRESHOLD ||
    estimate.matchTier === JobApplicantCountEstimateMatchTier.LOW ||
    estimate.matchTier === JobApplicantCountEstimateMatchTier.GLOBAL
  );
}

export function isLowApplicantEstimate(estimate: JobApplicantCountEstimate): boolean {
  return estimate.median < LOW_ESTIMATE_MEDIAN_THRESHOLD;
}

export function isSelfServeInReview(serviceLevel?: string | null, stage?: string | null): boolean {
  return serviceLevel === ServiceLevel.SELF_SERVE && stage === PostStage.IN_REVIEW;
}

export function shouldShowCoachingCopy(
  estimate: JobApplicantCountEstimate,
  serviceLevel?: string | null,
  stage?: string | null
): boolean {
  return isSelfServeInReview(serviceLevel, stage) && isLowApplicantEstimate(estimate);
}

export function shouldShowExecuSearchCopy(serviceLevel?: string | null): boolean {
  return isExecuSearchServiceLevel(serviceLevel);
}

export function formatApplicantCountRange(low: number, high: number): string {
  return `${low}–${high}`;
}

export function formatSampleSizeCopy(sampleSize: number): string {
  const postsLabel = sampleSize === 1 ? "post" : "posts";
  return `Based on ${sampleSize} similar ${postsLabel} (PGA.org, last 12 months).`;
}

export function matchTierTagType(tier: JobApplicantCountEstimateMatchTier): TagType {
  switch (tier) {
    case JobApplicantCountEstimateMatchTier.EXACT:
    case JobApplicantCountEstimateMatchTier.HIGH:
      return "success";
    case JobApplicantCountEstimateMatchTier.MEDIUM:
      return "information";
    case JobApplicantCountEstimateMatchTier.LOW:
    case JobApplicantCountEstimateMatchTier.GLOBAL:
      return "warn";
  }
}
